import { useState, useEffect } from 'react';
import type { IFrameProps } from '@graphcms/rich-text-types';
import { Box } from 'braid-design-system';
import { useScreenSize } from '../hooks/useScreenSize';
import { escapeHtml } from '../utils/escapeHtml';

interface IframeMessage {
  type: string;
  height: number;
}

export function YouTubeShorts({ url, width, height }: Partial<IFrameProps>) {
  const [iframeHeight, setIframeHeight] = useState(
    height ? `${height}px` : '350px',
  );
  const [thumbnailError, setThumbnailError] = useState(false);
  const { isMobile } = useScreenSize();

  // Extract video ID early for the thumbnail check
  const videoIdMatch = url?.match(/embed\/([^\s?/]+)/);
  const videoId = videoIdMatch ? videoIdMatch[1] : '';
  const thumbnailUrl = videoId
    ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    : '';

  // Resize message handler
  useEffect(() => {
    const handleResizeMessage = (event: MessageEvent<IframeMessage>) => {
      if (
        event.data.type === 'resize' &&
        typeof event.data.height === 'number'
      ) {
        setIframeHeight(`${event.data.height}px`);
      }
    };
    window.addEventListener('message', handleResizeMessage);
    return () => window.removeEventListener('message', handleResizeMessage);
  }, []);

  // Thumbnail loading check
  useEffect(() => {
    if (!thumbnailUrl) {
      setThumbnailError(true);
      return;
    }

    const img = new Image();
    img.onload = () => setThumbnailError(false);
    img.onerror = () => setThumbnailError(true);
    img.src = thumbnailUrl;
  }, [thumbnailUrl]);

  // Early returns after all hooks
  if (!url) {
    return null;
  }

  if (!videoId) {
    return (
      <Box marginY="medium">
        <iframe
          role="iframe"
          allowFullScreen
          style={{ border: 'none' }}
          width="100%"
          height={iframeHeight}
          src={escapeHtml(url)}
        />
      </Box>
    );
  }

  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;

  return (
    <Box marginY="medium">
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          width: '100%',
          maxWidth: width,
          aspectRatio: '16/9',
        }}
      >
        {!isMobile && (
          <div
            style={{
              zIndex: 1,
              position: 'absolute',
              width: '100%',
              height: '100%',
              background: thumbnailError ? '#000000' : `url(${thumbnailUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div
              style={{
                zIndex: 1,
                position: 'absolute',
                width: '100%',
                height: '100%',
                backdropFilter: thumbnailError ? 'none' : 'blur(24px)',
              }}
            />
          </div>
        )}
        <iframe
          src={escapeHtml(embedUrl)}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{
            zIndex: 2,
            aspectRatio: '9/16',
            height: '100%',
            minHeight: '350px',
            border: '0',
          }}
          title={`YouTube Shorts video ${videoId}`}
        />
      </div>
    </Box>
  );
}
